import React, { useState, useEffect } from 'react';
import { Card } from 'reactstrap';
import { Link, useRouteMatch } from 'react-router-dom';
import { startCase, compact } from 'lodash';
import { PageTitle, PseudoStates, useFetch, useFeatures, Button } from '@cordata/buffet';
import { all, spread } from 'axios';
import { format } from 'date-fns';
import camelcaseKeys from 'camelcase-keys';

import { authorizedInstance as axios } from '~/utils';

import { Table } from '~/components/MedicalEvent';

const showThis = (pr) =>
  (pr ? pr.show : false);

const transformEvent = (r, medicalEvent) => {
  const detailObject = camelcaseKeys(JSON.parse(r.attributes.detailObject));
  let combinedDateTime;

  if (medicalEvent.attributes.medicalEventName === 'cit') {
    combinedDateTime = detailObject.dateTime;
  } else {
    const [dt] = r.attributes.eventDate.split('.');
    const [d, t] = dt.split('T');
    const [y, m, da] = d.split('-');

    if (showThis(medicalEvent.attributes.propertyRules.eventTime)) {
      combinedDateTime = new Date([y, m, da, t]);
    } else {
      combinedDateTime = new Date([y, m, da]);
    }
  }

  return {
    ...r,
    attributes: {
      ...r.attributes,
      detailObject,
      combinedDateTime,
    },
  };
};

const MedicalEventsIndex = () => {
  const { hasAppCit } = useFeatures();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState([]);
  const [error, setError] = useState();
  const match = useRouteMatch();
  const [, patientId,, refMedicalEventName] = compact(match.url.split('/'));
  const [, aMedicalEvent] = useFetch('data/reference/reference-medical-event', {
    params: { medicalEventName: refMedicalEventName },
    instance: 'p',
  });
  const [medicalEvent, setMedicalEvent] = useState({ attributes: { propertyRules: {} } });
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    if (aMedicalEvent) {
      setMedicalEvent(aMedicalEvent.map(r => ({
        ...r,
        attributes: {
          ...r.attributes,
          propertyRules: JSON.parse(r.attributes.propertyRules),
        },
      }))[0]);
      setStatus('success');
    }
  }, [aMedicalEvent]);

  useEffect(() => {
    if (status === 'success') {
      all([
        axios
          .get(`/patients/${patientId}/medical-events/${medicalEvent.attributes.medicalEventName}`),
        axios
          .get(`/patients/${patientId}/foreign/medical-event`),
      ]).then(spread((n, f) =>
        [].concat(
          n.data.data.map(r => transformEvent(r, medicalEvent)),
          f.data.data
            .filter(ff =>
              ff.attributes.medicalEventName === medicalEvent.medicalEventName)
            .map(r => transformEvent(r, medicalEvent)),

        ).map((mE) => {
          if (mE.type === 'nav-list-view-medical-event') {
            /* eslint-disable-next-line no-param-reassign */
            mE.isForeign = true;
          }

          return mE;
        })))
        .then(setData)
        .catch(setError)
        .then(() => setIsLoading(false));
    }
  }, [status]);

  return status === 'success' ? (
    <>
      <PageTitle
        action={!hasAppCit
          ? (
            <Button
              outline
              tag={Link}
              to={`/patients/${patientId}/medical-events/${refMedicalEventName}/add`}
              icon="plus"
            >
              Add {startCase(medicalEvent.attributes.medicalEventLabel)}
            </Button>
         ) : null
        }
      >
        {`${startCase(medicalEvent.attributes.medicalEventLabel)}s`}
      </PageTitle>

      <Card>
        {!isLoading && !error ? (
          <Table
            defaultSorted={[{
              id: 'combinedDateTime', desc: true,
            }]}
            columns={[{
              id: 'combinedDateTime',
              Header: 'Date',
              accessor: x =>
              format(
                x.attributes.combinedDateTime,
                showThis(medicalEvent.attributes.propertyRules.eventTime)
                ? 'MM/DD/YYYY hh:mm a'
                : 'MM/DD/YYYY',
              ),
            }, {
              id: 'status',
              Header: 'Status',
              accessor: 'relationships.referenceStatus.attributes.statusLabel',
              show: showThis(medicalEvent.attributes.propertyRules.status),
            }, {
              id: 'reason',
              Header: 'Reason',
              accessor: 'relationships.referenceReason.attributes.reasonLabel',
              show: showThis(medicalEvent.attributes.propertyRules.reason),
            }, {
              id: 'wasOverdoseFatal',
              Header: 'Fatal',
              accessor: 'attributes.detailObject.wasOverdoseFatal',
              show: medicalEvent.attributes.medicalEventName === 'drug_overdose',
            }, {
              id: 'naloxoneAdministered',
              Header: 'Naloxone Administered',
              accessor: 'attributes.detailObject.naloxoneAdministered',
              show: medicalEvent.attributes.medicalEventName === 'drug_overdose',
            }, {
              id: 'wasTransportedToHospital',
              Header: 'Was Transported To Hospital',
              accessor: 'attributes.detailObject.wasTransportedToHospital',
              show: medicalEvent.attributes.medicalEventName === 'drug_overdose',
            }, {
              id: 'contactAttemptType',
              Header: 'Channel',
              accessor: x => startCase(x.attributes.detailObject.contactAttemptType),
              show: medicalEvent.attributes.medicalEventName === 'survivor_interaction',
            }, {
              id: 'survivorContacted',
              Header: 'Survivor Contacted',
              accessor: 'attributes.detailObject.survivorContacted',
              show: medicalEvent.attributes.medicalEventName === 'survivor_interaction',
            }, {
              id: 'kitsDistributed',
              Header: 'Kits Distributed',
              accessor: 'attributes.detailObject.kitsDistributed',
              show: medicalEvent.attributes.medicalEventName === 'survivor_interaction',
            }, {
              id: 'method',
              Header: 'Channel',
              accessor: x => startCase(x.attributes.detailObject.method),
              show: medicalEvent.attributes.medicalEventName === 'other_interaction',
            }, {
              id: 'name',
              Header: 'Name',
              accessor: 'attributes.detailObject.name',
              show: medicalEvent.attributes.medicalEventName === 'other_interaction',
            }, {
              id: 'facility',
              Header: 'Facility',
              accessor: 'relationships.facility.attributes.facilityName',
              show: medicalEvent.attributes.medicalEventName === 'treatment_referral',
            }, {
              id: 'officer',
              Header: 'Officer',
              accessor: 'attributes.detailObject.officer',
              show: medicalEvent.attributes.medicalEventName === 'cit',
            }, {
              id: 'crisis',
              Header: 'Crisis',
              accessor: 'attributes.detailObject.crisis',
              show: medicalEvent.attributes.medicalEventName === 'cit',
            }, {
              id: 'response',
              Header: 'Response',
              accessor: 'attributes.detailObject.response',
              show: medicalEvent.attributes.medicalEventName === 'cit',
            }, {
              id: 'disposition',
              Header: 'Disposition',
              accessor: 'attributes.detailObject.dispositionLabel',
              show: medicalEvent.attributes.medicalEventName === 'cit',
            }]}
            data={data}
            loading={isLoading}
          />
        ) : (
          <PseudoStates isLoading={isLoading} error={error} />
        )}
      </Card>
    </>
  ) : (
    <PseudoStates isLoading={status === 'loading'} error={status === 'error'} />
  );
};

export default MedicalEventsIndex;
